import { Box, Flex, IconButton, Text, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Spinner } from '@chakra-ui/react';
import { useHlsPlayer } from '../hooks/useHlsPlayer';
import { useTranslation } from 'react-i18next';

interface AudioPlayerProps {
  streamUrl: string | null;
  trackTitle?: string;
  trackArtist?: string;
}

export function AudioPlayer({ streamUrl, trackTitle, trackArtist }: AudioPlayerProps) {
  const { t } = useTranslation();
  const { audioRef, isPlaying, isLoading, error, toggle } = useHlsPlayer({
    url: streamUrl,
    autoPlay: true,
  });

  return (
    <Box
      bg="gray.800"
      borderRadius="lg"
      p={6}
      color="white"
      data-testid="audio-player"
    >
      <audio ref={audioRef} />

      {/* Track info */}
      <Box mb={4} textAlign="center">
        <Text fontSize="lg" fontWeight="bold" noOfLines={1}>
          {trackTitle || t('player.noTrack')}
        </Text>
        {trackArtist && (
          <Text fontSize="sm" color="gray.400" noOfLines={1}>
            {trackArtist}
          </Text>
        )}
      </Box>

      {/* Controls */}
      <Flex justify="center" align="center" gap={4}>
        {isLoading ? (
          <Spinner size="lg" color="white" />
        ) : (
          <IconButton
            aria-label={isPlaying ? t('player.pause') : t('player.play')}
            onClick={toggle}
            isRound
            size="lg"
            colorScheme="blue"
            icon={
              <Text fontSize="xl">{isPlaying ? '⏸' : '▶'}</Text>
            }
            data-testid="play-pause-button"
          />
        )}
      </Flex>

      {/* Volume slider */}
      <Box mt={4} px={4}>
        <Slider
          aria-label={t('player.volume')}
          defaultValue={100}
          min={0}
          max={100}
          onChange={(val) => {
            if (audioRef.current) {
              audioRef.current.volume = val / 100;
            }
          }}
        >
          <SliderTrack bg="gray.600">
            <SliderFilledTrack bg="blue.400" />
          </SliderTrack>
          <SliderThumb />
        </Slider>
      </Box>

      {/* Error display */}
      {error && (
        <Text color="red.300" fontSize="sm" mt={3} textAlign="center">
          {t(error)}
        </Text>
      )}
    </Box>
  );
}
